//Dealer Franchisee subclass views
import {
  combineStatesAsList,
  flagListEquals,
  isEmptyOrSpace
} from '../../../../webmodule-common/other/ui/string-helper-functions';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import { createNewQuote } from '../quote-creator';
import { customElement, state } from 'lit/decorators.js';
import { defaultStagedThreshold, EventCancellation } from '../../../../webmodule-common/other/general/staged-event';
import { displaySupplierTACNotApprovedMsg } from '../../../v6config/supplier-services';
import { EventSnippet, NullPromise } from '../../../../webmodule-common/other/ui/events';

import { FranchiseeApi } from '../../../api/franchisee-api';
import { FranchiseeQuoteSummaryTable } from './franchisee-quote-summary-table';
import { FranchiseeV6BranchQuoteSupplierDefaultsView } from './v6/franchisee-v6-branchdefaults';
import { getApiFactory } from '../../../api/api-injector';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';

import { getQuoteContainer, getQuoteContainerManager } from '../quote-ui-adapter';
import { getSupplier } from '../supplier-selector';
import { html } from 'lit';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';

import { notificationSignal } from '../../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { QuoteContainer, QuoteContainerManager } from '../../../quotes/data/quote-container';
import { QuoteListView, QuoteSummaryTable, QuoteSummaryTableOptions } from '../../../quotes/views/quote-list-view';
import { QuoteState, ViewQuoteSummary, ViewQuoteTotalByState } from '../../../api/dealer-api-interface-quote';
import { SSEQuote } from '../../../quotes/data/sse-quote';
import { stagedExecution } from '../../../../webmodule-common/other/general/event-source-client';
import { userDataStore } from '../../common/current-user-data-store';
import { WMEventSource } from '../../../api/event-source';
import type { TabPage } from '../../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { QuoteListPageFragments } from '../quote-list-page-fragment-constants';

const numberOfDaysHistory = 30;

@customElement('wm-franchiseequotelistview')
export class FranchiseeQuoteListView extends QuoteListView {
  franchiseeApi: FranchiseeApi = getApiFactory().franchisee();
  @state()
  quoteTotals: ViewQuoteTotalByState[] | null = null;
  private staleQuotes: string[] = [];

  @state()
  quoteDefaultsTab = true;
  @state()
  quoteDefaultsUI?: FranchiseeV6BranchQuoteSupplierDefaultsView;
  async init() {
    await this.updatePageTotals();
    const table = this.tabManager.activePage?.data as QuoteSummaryTable;
    if (table) await table.refreshData(1);
  }

  async doAfterQuoteEdit() {
    await this.updatePageTotals();
  }

  private _eventCancel: EventCancellation = new EventCancellation();
  private _eventQuoteUpdated = stagedExecution({
    threshold: defaultStagedThreshold,
    cancelToken: this._eventCancel,
    eventFinally: () => notificationSignal(false),
    event: async () => {
      this.updatePageTotals();
      this.tables.forEach(t => (t.dataIsStale = true));
      const table = this.tabManager.activePage?.data as QuoteSummaryTable;
      this.staleQuotes = [];
      if (table) {
        await table.refreshData();
        table.dataIsStale = false;
      }
      //reload the page.. we dont know what moved, or where, or what state its in
    },
    eventTriggered: (_data: SSEQuote) => {
      notificationSignal(true);
    },
    eventActioned: async (data: SSEQuote) => {
      if (!this.staleQuotes.includes(data.id)) this.staleQuotes.push(data.id);
      const table = this.tabManager.activePage?.data as QuoteSummaryTable;
      if (table) {
        await table.refreshData();
        table.dataIsStale = false;
      }
      // Copy completion toast now handled globally via registerQuoteCopyCompletionMonitor
    }
  });

  connectedCallback(): void {
    super.connectedCallback();
    WMEventSource.getInstance().addEventListener(WMEventSource.quote, this._eventQuoteUpdated);
    this.addEventListener('quote-filter-changed', this.handleFilterChanged);
  }
  private handleFilterChanged = () => {
    this.updatePageTotals();
  };

  disconnectedCallback(): void {
    super.disconnectedCallback();
    WMEventSource.getInstance().removeEventListener(WMEventSource.quote, this._eventQuoteUpdated);
    this.removeEventListener('quote-filter-changed', this.handleFilterChanged);
  }
  // Just to update the count
  private getTabTitle(caption: string, state: QuoteState[], pageFragment: string) {
    const table = this.tables.find(t => t instanceof FranchiseeQuoteSummaryTable && t.pageFragment === pageFragment) as
      | FranchiseeQuoteSummaryTable
      | undefined;

    const showAllQuotes = table?.showAllQuotesFlag ?? true;
    const filterUserId = !showAllQuotes ? (table?.filterAssignedToUserId ?? getCurrentUser()?.id ?? null) : null;

    let totalsForState = this.quoteTotals?.filter(x => flagListEquals(state, x.quoteState)) ?? [];

    // Apply "My Quotes" only for the tab whose radio is set to My Quotes
    if (filterUserId) {
      totalsForState = totalsForState.filter(x => x.userId === filterUserId);
    }

    const count = totalsForState.reduce((sum, x) => sum + x.quoteCount, 0);
    return html`${caption} (${count})`;
  }

  protected createQuoteContainerManager(quoteContainer: QuoteContainer): QuoteContainerManager {
    return getQuoteContainerManager(quoteContainer);
  }

  protected async updatePageTotals() {
    const ownerId = await this.quoteOwnerId();
    const isFab = userDataStore.isFabricatorMode;

    const allCurrentStates = combineStatesAsList(
      QuoteState.Draft,
      QuoteState.Active,
      QuoteState.Active + QuoteState.Copying,
      QuoteState.Draft + QuoteState.Copying,
      ...(isFab ? [QuoteState.Accepted, QuoteState.Approved] : [])
    );

    const archivedStates = combineStatesAsList(
      ...(isFab ? [] : [QuoteState.Approved]),
      QuoteState.Rejected,
      QuoteState.Accepted_RefusedBySupplier,
      QuoteState.Cancelled,
      QuoteState.Lapsed
    );

    const middleOptions = isFab
      ? [
          {
            state: combineStatesAsList(QuoteState.Approved),
            numberOfDaysHistory: null
          }
        ]
      : [
          {
            state: combineStatesAsList(QuoteState.Accepted),
            numberOfDaysHistory: null
          },
          {
            state: combineStatesAsList(QuoteState.Accepted_AssignedToReviewer, QuoteState.SupplierReviewPending),
            numberOfDaysHistory: null
          },
          {
            state: combineStatesAsList(QuoteState.SupplierReviewed),
            numberOfDaysHistory: null
          }
        ];

    const totals = await this.quoteApi.getQuoteTotalsByState({
      quoteOwnerId: [ownerId],
      options: [
        {
          state: allCurrentStates,
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(QuoteState.Draft, QuoteState.Draft + QuoteState.Copying),
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(QuoteState.Active, QuoteState.Active + QuoteState.Copying),
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(QuoteState.Issued, QuoteState.IssuePending, QuoteState.ResetInProgress),
          numberOfDaysHistory: null
        },
        ...middleOptions,
        {
          state: archivedStates,
          numberOfDaysHistory: numberOfDaysHistory
        }
      ]
    });
    if (totals) this.quoteTotals = totals.totals.filter(x => x.quoteOwnerId === ownerId);
    this.tabManager.requestUpdate();
  }

  protected createQuoteContainer(quoteId: string): QuoteContainer {
    return getQuoteContainer(quoteId);
  }

  //this can be overridden in sub class to replace the table with different column/view etc
  protected quoteSummaryTableFactory(options: QuoteSummaryTableOptions): FranchiseeQuoteSummaryTable {
    return new FranchiseeQuoteSummaryTable(options);
  }

  beforeRefresh() {
    this.staleQuotes = [];
  }

  afterRefresh() {
    this._eventCancel.cancel();
  }

  async dispose(): Promise<void> {
    await super.dispose();
    this._eventCancel.cancel();
  }

  protected createSummaryTable(
    title: EventSnippet,
    quoteStates: QuoteState[],
    pageFragment: string,
    numberOfDays?: number
  ): QuoteSummaryTable {
    return this.quoteSummaryTableFactory({
      quoteOwnerId: this.quoteOwnerId,
      quoteStates: quoteStates,
      numberOfDaysHistory: numberOfDays,
      customerCache: this.customerCache,
      userProfileCache: this.userProfileCache,
      title: title,
      copyQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.copyQuote(quoteSummary);
      },
      deleteQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.deleteQuote(quoteSummary);
      },
      // The row-action dialog refreshes its own table; everything OUTSIDE the table is refreshed
      // here. `quoteSummary.state` is the state the row had BEFORE the action, which is exactly what
      // refreshTabsForState needs — the tab it is leaving. The destination tab and the totals are
      // covered by doAfterQuoteEdit/updatePageTotals.
      quoteRowActionCompletedEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.refreshTabsForState(quoteSummary.state);
        await this.doAfterQuoteEdit();
      },
      pageFragment: pageFragment,
      stale: { exists: (id: string) => this.staleQuotes.includes(id) }
    });
  }

  //override this in a subclass to change the design, and order of the tables
  protected initializeTables(): QuoteSummaryTable[] {
    const makeTable = (titleStr: () => string, state: QuoteState[], pageFragment: string, numberOfDays?: number) => {
      // to resolve the count for each tab
      return this.createSummaryTable(
        () => this.getTabTitle(titleStr(), state, pageFragment),
        state,
        pageFragment,
        numberOfDays
      );
    };

    const isFab = userDataStore.isFabricatorMode;

    const allCurrentStates = combineStatesAsList(
      QuoteState.Draft,
      QuoteState.Active,
      QuoteState.Copying + QuoteState.Draft,
      QuoteState.Copying + QuoteState.Active,
      ...(isFab ? [QuoteState.Accepted, QuoteState.Approved] : [])
    );

    const archivedStates = combineStatesAsList(
      ...(isFab ? [] : [QuoteState.Approved]),
      QuoteState.Rejected,
      QuoteState.Accepted_RefusedBySupplier,
      QuoteState.Cancelled,
      QuoteState.Lapsed
    );

    return [
      makeTable(
        () => LangFranchiseeQuoteListView.allCurrentTableTitle(),
        allCurrentStates,
        QuoteListPageFragments.ALL_CURRENT,
        undefined
      ),
      makeTable(
        () => LangFranchiseeQuoteListView.draftTableTitle(),
        combineStatesAsList(QuoteState.Draft, QuoteState.Draft + QuoteState.Copying),
        QuoteListPageFragments.DRAFT,
        undefined
      ),
      makeTable(
        () => LangFranchiseeQuoteListView.activeTableTitle(),
        combineStatesAsList(QuoteState.Active, QuoteState.Active + QuoteState.Copying),
        QuoteListPageFragments.ACTIVE,
        undefined
      ),
      makeTable(
        () => LangFranchiseeQuoteListView.issuedTableTitle(),
        combineStatesAsList(QuoteState.Issued, QuoteState.IssuePending, QuoteState.ResetInProgress),
        QuoteListPageFragments.ISSUED,
        undefined
      ),

      // Fabricator mode: hide Accepted (bypassed), show Approved tab instead
      // Non-fabricator mode: show Accepted, Under Review, Reviewed tabs
      ...(isFab
        ? [
            makeTable(
              () => LangFranchiseeQuoteListView.fabricatedTableTitle(),
              combineStatesAsList(QuoteState.Approved),
              QuoteListPageFragments.APPROVED,
              undefined
            )
          ]
        : [
            makeTable(
              () => LangFranchiseeQuoteListView.acceptedTableTitle(),
              combineStatesAsList(QuoteState.Accepted),
              QuoteListPageFragments.ACCEPTED,
              undefined
            ),
            makeTable(
              () => LangFranchiseeQuoteListView.underReviewTableTitle(),
              combineStatesAsList(QuoteState.Accepted_AssignedToReviewer, QuoteState.SupplierReviewPending),
              QuoteListPageFragments.UNDER_REVIEW,
              undefined
            ),
            makeTable(
              () => LangFranchiseeQuoteListView.reviewedTableTitle(),
              combineStatesAsList(QuoteState.SupplierReviewed),
              QuoteListPageFragments.REVIEWED,
              undefined
            )
          ]),

      makeTable(
        () => LangFranchiseeQuoteListView.archivedTableTitle(),
        archivedStates,
        QuoteListPageFragments.ARCHIVED,
        numberOfDaysHistory
      )
    ];
  }

  protected async createNewQuote(): NullPromise<QuoteContainer> {
    const supplier = await getSupplier();
    if (isEmptyOrSpace(supplier.supplierId)) return null;
    if (!userDataStore.supplierTACApproved(supplier.supplierId)) {
      await displaySupplierTACNotApprovedMsg(supplier, true);
      return null;
    }
    const owner = await this.quoteOwnerId();

    return createNewQuote({ supplierId: supplier.supplierId, owner: owner });
  }

  protected getInitialPages(): TabPage[] {
    const results = super.getInitialPages();
    results.push(this.makeQuoteDefaultsPage());
    return results;
  }
  makeQuoteDefaultsPage(): TabPage {
    const page: TabPage = {
      caption: () => LangFranchiseeQuoteListView.quoteDefaultsTab(),
      canLeave: async () => {
        return (await this.quoteDefaultsUI?.saveChanges()) ?? true;
      },
      onEnter: async () => {
        this.quoteDefaultsUI ??= await constructAsync(new FranchiseeV6BranchQuoteSupplierDefaultsView());

        //we need to load the defaults data from the server.
      },
      content: () => {
        return html`${this.quoteDefaultsUI}`;
      },
      fragment: QuoteListPageFragments.QUOTE_DEFAULTS,
      secondaryButtons: () => this.quoteDefaultsUI?.buttonMenu() ?? ''
    };
    return page;
  }
}
